const CHUNK_ERROR_RELOAD_KEY = 'chunk_error_reload';
const GUARD_TTL_MS = 10_000;

type ChunkErrorDeps = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  reload: () => void;
  now: () => number;
};

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /loading chunk .* failed/i.test(error.message) ||
    /failed to fetch dynamically imported module/i.test(error.message)
  );
}

function getDefaultDeps(): ChunkErrorDeps {
  return {
    getItem: (key) => globalThis.sessionStorage?.getItem(key) ?? null,
    setItem: (key, value) => {
      globalThis.sessionStorage?.setItem(key, value);
    },
    removeItem: (key) => {
      globalThis.sessionStorage?.removeItem(key);
    },
    reload: () => {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    },
    now: () => Date.now(),
  };
}

/**
 * Detects ChunkLoadError and performs a one-time page reload to recover
 * from stale chunk references after a deployment. Uses sessionStorage
 * to guard against infinite reload loops (with a 10s TTL so future
 * deployments can still trigger recovery).
 *
 * @returns true if a reload was triggered (caller should skip further error handling)
 */
export function handleChunkError(error: Error, deps: ChunkErrorDeps = getDefaultDeps()): boolean {
  try {
    if (!isChunkLoadError(error)) {
      return false;
    }

    const now = deps.now();
    const timestamp = deps.getItem(CHUNK_ERROR_RELOAD_KEY);
    if (!timestamp || now - Number(timestamp) > GUARD_TTL_MS) {
      deps.setItem(CHUNK_ERROR_RELOAD_KEY, String(now));
      deps.reload();
      return true;
    }

    return false;
  } catch {
    return false;
  }
}
